import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { api } from '@/api/repository/axios';
import { isLoggedInAtom } from '@/state/loginState';
import { unreadNotificationCountAtom } from '@/state/notificationState';

/**
 * 폴링 간격. 운영 로그에서 이 API 가 전체 호출의 절반 이상(이틀 5,211회)이라 30초 → 60초로 늘렸다.
 * 새 알림은 푸시로도 오고, 화면이 다시 보일 때 즉시 갱신하므로 배지 지연은 체감되지 않는다.
 */
const POLL_INTERVAL_MS = 60_000;

/** 화면(탭·앱)이 보이는 상태인지 — 숨김 상태에서는 폴링 요청을 보내지 않는다 */
const isPageVisible = (): boolean => document.visibilityState === 'visible';

/**
 * 안 읽은 알림 수를 로그인 상태에서 60초마다(화면이 보일 때만), 그리고 탭이 다시 보일 때 갱신한다.
 * 여러 컴포넌트가 호출해도 요청은 마운트된 훅 수만큼 나가므로 헤더 한 곳에서만 폴링하고,
 * 나머지는 `unreadNotificationCountAtom` 만 읽는다.
 */
export function useUnreadNotificationsPolling(): void {
    const isLoggedIn = useRecoilValue(isLoggedInAtom);
    const [, setCount] = useRecoilState(unreadNotificationCountAtom);

    const refresh = useCallback(async () => {
        try {
            const res = await api.user.getUnreadNotificationCount();
            setCount(res.result?.count ?? 0);
        } catch {
            // 비로그인/네트워크 오류 — 배지는 그대로 둔다
        }
    }, [setCount]);

    useEffect(() => {
        if (!isLoggedIn) {
            setCount(0);
            return;
        }
        refresh();
        // 백그라운드 탭·앱에서는 건너뛴다 — 보이지 않는 배지를 위해 로그인 확인(DB 조회)까지 매번 돌 필요가 없다
        const timer = window.setInterval(() => { if (isPageVisible()) refresh(); }, POLL_INTERVAL_MS);
        const onVisible = () => { if (isPageVisible()) refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [isLoggedIn, refresh, setCount]);
}
